'use client';

import { Card } from '@app/app/_components/Card/Card';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { Select } from '@app/app/_components/Input/Select/Select';
import { DiscountCode } from '@app/app/esales/travel/_sections/DiscountCode';
import { PropertyRegistryResult } from '@app/domain/model/eshop/home/PropertyRegistryResult';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { Message } from '@app/app/_components/Message/Message';
import { CommonType } from '@app/domain/model/CommonType';
import { Button } from '@app/app/_components/Button/Button';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { ProductDataLeasing } from '@app/domain/model/eshop/transport/ProductDataLeasing';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseHomeInsuranceResult } from '@app/service/eshop/home/actions/purchaseHomeInsurance';
import { Address } from '@app/domain/model/Address';
import { formatAddress } from '@app/lib/address-formatter/formatAddress';
import { ProductDataObject } from '@app/domain/model/eshop/home/ProductDataObject';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { twMerge } from 'tailwind-merge';

interface Props {
    formState: FormState<PurchaseHomeInsuranceResult>;
    propertyInfo: Nullable<PropertyRegistryResult>;
    address: Nullable<Address>;
    discountCodeData: DiscountCodeData;
    handleDiscountCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleApplyDiscountClick: () => void;
    leasings: ProductDataLeasing[];
    handlePledgedToBankChange: (value: string) => void;
    pledgedToBank: Nullable<string>;
    selectedLeasing: Nullable<string>;
    handleSelectedLeasingChange: (value: string) => void;
    objects: ProductDataObject[];
    purposes: Nullable<ProductDataPurpose>;
}

export function ObjectSummaryReadOnlyStep({
    propertyInfo,
    discountCodeData,
    handleDiscountCode,
    handleApplyDiscountClick,
    leasings,
    formState,
    address,
    handlePledgedToBankChange,
    pledgedToBank,
    selectedLeasing,
    handleSelectedLeasingChange,
    objects,
    purposes,
}: Props) {
    const t = useTranslations();
    const locale = useLocale();
    const parameters = useAppParameters();

    const pledgedOptions = [
        { label: t('general.yes'), value: CommonType.Yes },
        { label: t('general.no'), value: CommonType.No },
    ];

    const leasingsOptions = useMemo(() => {
        if (!leasings || leasings.length === 0) {
            return [];
        }
        return leasings.map(leasing => ({
            label: leasing.name,
            value: leasing.code,
        }));
    }, [leasings]);

    const selectedLeasingName = useMemo(() => {
        if (!selectedLeasing) {
            return null;
        }
        const leasing = leasings.find(leasing => leasing.code === selectedLeasing);
        return leasing ? leasing.name : null;
    }, [leasings, selectedLeasing]);

    const constructionTypeOptions = useMemo(
        () => purposes?.purposes.map(purpose => ({ label: purpose.name, value: purpose.code })) ?? [],
        [purposes],
    );

    if (!propertyInfo) {
        return <Message variant="warning">{t('eshop.home.objectSummary.noPropertyInfo')}</Message>;
    }

    const policyholder = propertyInfo.policyholder;

    const ownerName = `${policyholder.name} ${policyholder.lastName}`;

    const totalArea = propertyInfo.enclosedArea;
    const constructionYear = propertyInfo.constructionYear;

    const objectType = objects.find(obj => obj.code === propertyInfo.objectType)?.name;
    const typeOfConstruction =
        purposes?.purposes.find(purpose => purpose.code === propertyInfo.typeOfConstruction)?.name ?? null;

    return (
        <div className="flex flex-col gap-8">
            <Message
                containerClassName="items-center"
                variant="info"
                addon={
                    <Button
                        variant="outlined"
                        preventDefault
                        size="tiny"
                        className="w-max"
                        target="_blank"
                        href={
                            locale === 'lt'
                                ? parameters.eshopHomeInsuranceInfoUrls.contactUs
                                : parameters.eshopHomeInsuranceInfoUrls.contactUsEn
                        }
                        rightAddon={<ArrowRightIcon className="size-4" />}>
                        {t('general.contactUs')}
                    </Button>
                }>
                {t('eshop.home.objectSummary.multipleObjectsExist')}
            </Message>
            <Card className="gap-2 p-6">
                <CollapsablePanel
                    label={`${formatAddress(address) || t('eshop.home.objectSummary.property')}`}
                    collapsable={false}>
                    <div className="grid grid-cols-1 gap-4 px-6 pt-2 md:grid-cols-2">
                        <ReadOnlyField label={t('eshop.home.objectSummary.owner')}>{ownerName}</ReadOnlyField>

                        <ReadOnlyField label={t('eshop.home.objectSummary.personalCode')}>
                            {policyholder.code}
                        </ReadOnlyField>

                        <ReadOnlyField
                            className={twMerge(!typeOfConstruction && 'col-span-2')}
                            label={t('eshop.home.objectSummary.objectType')}>
                            {objectType || '-'}
                        </ReadOnlyField>

                        {typeOfConstruction && (
                            <ReadOnlyField label={t('eshop.home.objectSummary.propertyType')}>
                                {typeOfConstruction}
                            </ReadOnlyField>
                        )}

                        <ReadOnlyField label={t('eshop.home.objectSummary.constructionYear')}>
                            {constructionYear || '-'}
                        </ReadOnlyField>

                        <ReadOnlyField label={t('eshop.home.objectSummary.totalArea')}>
                            {totalArea ? `${totalArea} ${t('eshop.home.objectSummary.squareMeters')}` : '-'}
                        </ReadOnlyField>

                        {!typeOfConstruction && (
                            <Select
                                className="col-span-2"
                                options={constructionTypeOptions}
                                error={formState.violations?.constructionType}
                                name="constructionType"
                                label={t('eshop.home.objectSummary.constructionType')}
                                placeholder={t('eshop.general.select')}
                                defaultValue={purposes?.defaultPurpose}
                            />
                        )}
                    </div>
                </CollapsablePanel>

                <div className="flex flex-col gap-4">
                    <CollapsablePanel
                        label={t('eshop.home.objectSummary.pledgedToBank')}
                        collapsable={false}
                        buttonClassName="p-0 bg-transparent shadow-none">
                        <div className="flex flex-col gap-4 px-6 pt-2">
                            <Select
                                options={pledgedOptions}
                                error={formState.violations?.pledgedToBank}
                                name="pledgedToBank"
                                value={pledgedToBank ?? ''}
                                onChange={handlePledgedToBankChange}
                                label={t('eshop.home.objectSummary.answer')}
                                placeholder={t('eshop.general.select')}
                            />
                            {pledgedToBank === CommonType.Yes && leasingsOptions.length > 0 && (
                                <Select
                                    options={leasingsOptions}
                                    label={t('eshop.home.objectSummary.leasing')}
                                    error={formState.violations?.leasing}
                                    name="leasing"
                                    value={selectedLeasing ?? ''}
                                    onChange={handleSelectedLeasingChange}
                                    placeholder={t('eshop.general.select')}
                                />
                            )}
                            <input type="hidden" name="leasingName" value={selectedLeasingName ?? ''} />
                        </div>
                    </CollapsablePanel>
                </div>
            </Card>
            <DiscountCode
                handleDiscountCode={handleDiscountCode}
                handleApplyDiscountClick={handleApplyDiscountClick}
                discountCodeData={discountCodeData}
            />
        </div>
    );
}
